import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import LinkResourcesEditor from '@/components/LinkResourcesEditor';
import { useCreateExpertTip } from '@/hooks/useCreateExpertTip';
import { useActiveExpertProfiles } from '@/hooks/useExpertProfiles';
import { useActiveTopics } from '@/hooks/useExpertTipTopics';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import type { ExpertTipFormData, ExpertTipFormErrors } from '@/types/expert-tip';

const initialFormData: ExpertTipFormData = {
  title: '',
  body: '',
  expertId: '',
  topicId: '',
  sortOrder: '',
  linkResources: [],
};

function validateForm(data: ExpertTipFormData): ExpertTipFormErrors {
  const errors: ExpertTipFormErrors = {};

  if (!data.title.trim()) errors.title = 'Title is required';
  else if (data.title.length > 255) errors.title = 'Title must be 255 characters or less';

  if (!data.body.trim()) errors.body = 'Body is required';
  else if (data.body.length > 10000) errors.body = 'Body must be 10000 characters or less';

  if (!data.expertId) errors.expertId = 'Expert is required';

  if (data.sortOrder.trim()) {
    const sortNum = parseInt(data.sortOrder, 10);
    if (isNaN(sortNum) || sortNum < 0) errors.sortOrder = 'Sort order must be 0 or greater';
  }

  const hasInvalidLinks = data.linkResources.some(
    (lr) => (!lr.title.trim() && lr.url.trim()) || (lr.title.trim() && !lr.url.trim())
  );
  if (hasInvalidLinks) {
    errors.linkResources = 'Each link resource must have both a title and URL';
  }

  return errors;
}

export default function NewExpertTipPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ExpertTipFormData>(initialFormData);
  const [errors, setErrors] = useState<ExpertTipFormErrors>({});
  const createMutation = useCreateExpertTip();
  const { data: activeExperts } = useActiveExpertProfiles();
  const { data: activeTopics = [] } = useActiveTopics();
  const { user } = useAdminAuth();
  const isCreator = user?.role === 'creator';
  const creatorProfileId = user?.expertProfileId;
  const creatorHasNoProfile = isCreator && !creatorProfileId;

  useEffect(() => {
    document.title = 'New Expert Tip | Admin Dashboard';
    return () => {
      document.title = 'Admin Dashboard';
    };
  }, []);

  useEffect(() => {
    if (isCreator && creatorProfileId) {
      setFormData((prev) => ({ ...prev, expertId: creatorProfileId.toString() }));
    }
  }, [isCreator, creatorProfileId]);

  const handleFieldChange = (field: keyof ExpertTipFormData, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof ExpertTipFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the form errors');
      return;
    }

    try {
      await createMutation.mutateAsync(formData);
      navigate('/admin/expert-tips');
    } catch (error) {
      console.error('Failed to create expert tip:', error);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Back button */}
      <button
        onClick={() => navigate('/admin/expert-tips')}
        className="flex items-center gap-2 text-pine-ink-soft hover:text-pine-ink transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Expert Tips</span>
      </button>

      <h1 className="text-pine-display font-display font-semibold text-pine-ink">New Expert Tip</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">
                Title <span className="text-red-400">*</span>
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                placeholder="e.g. The importance of unstructured play"
              />
              {errors.title && <p className="text-sm text-red-400">{errors.title}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="body">
                Body <span className="text-red-400">*</span>
              </Label>
              <textarea
                id="body"
                value={formData.body}
                onChange={(e) => handleFieldChange('body', e.target.value)}
                placeholder="Write the expert tip content here..."
                rows={8}
                className="w-full px-3 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink resize-y focus:border-pine-pine focus:outline-none transition-colors"
              />
              {errors.body && <p className="text-sm text-red-400">{errors.body}</p>}
            </div>
          </CardContent>
        </Card>

        {/* Expert */}
        <Card>
          <CardHeader>
            <CardTitle>Expert</CardTitle>
          </CardHeader>
          <CardContent>
            {creatorHasNoProfile ? (
              <div className="p-4 rounded-pine-input bg-amber-500/10 border border-amber-500/20">
                <p className="text-sm text-amber-500">
                  No expert profile assigned. Contact an administrator to assign your expert profile before creating tips.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="expertId">
                  Expert <span className="text-red-400">*</span>
                </Label>
                <select
                  id="expertId"
                  value={formData.expertId}
                  onChange={(e) => handleFieldChange('expertId', e.target.value)}
                  disabled={isCreator && !!creatorProfileId}
                  className="w-full px-4 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink focus:border-pine-pine focus:outline-none transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <option value="">Select an expert...</option>
                  {activeExperts?.map((expert) => (
                    <option key={expert.id} value={expert.id}>
                      {expert.name} — {expert.subtitle}
                    </option>
                  ))}
                </select>
                {isCreator && creatorProfileId && (
                  <p className="text-xs text-pine-ink-soft">
                    Your expert profile is automatically selected.
                  </p>
                )}
                {errors.expertId && <p className="text-sm text-red-400">{errors.expertId}</p>}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Topic */}
        <Card>
          <CardHeader>
            <CardTitle>Topic (optional)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="topicId">Topic</Label>
              <select
                id="topicId"
                value={formData.topicId}
                onChange={(e) => handleFieldChange('topicId', e.target.value)}
                className="w-full px-4 py-2 rounded-pine-input border border-pine-line bg-white text-pine-ink focus:border-pine-pine focus:outline-none transition-colors"
              >
                <option value="">No topic</option>
                {activeTopics.map((topic) => (
                  <option key={topic.id} value={topic.id.toString()}>
                    {topic.title} ({topic.publishedTipCount} tips)
                  </option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Link Resources */}
        <Card>
          <CardHeader>
            <CardTitle>Link Resources</CardTitle>
          </CardHeader>
          <CardContent>
            <LinkResourcesEditor
              value={formData.linkResources}
              onChange={(resources) => handleFieldChange('linkResources', resources)}
              error={errors.linkResources}
            />
          </CardContent>
        </Card>

        {/* Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="sortOrder">Sort Order</Label>
              <Input
                id="sortOrder"
                type="number"
                min="0"
                value={formData.sortOrder}
                onChange={(e) => handleFieldChange('sortOrder', e.target.value)}
                placeholder="e.g. 1 (optional)"
                className="max-w-xs"
              />
              {errors.sortOrder && <p className="text-sm text-red-400">{errors.sortOrder}</p>}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={createMutation.isPending || creatorHasNoProfile}
            className="admin-button-primary"
          >
            {createMutation.isPending ? 'Creating...' : 'Create Expert Tip'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/expert-tips')}
            className="px-6 py-2.5 rounded-pine-input border border-pine-line text-pine-ink-soft hover:bg-pine-hover-item transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
